import React from 'react';
import { formatPrice } from '../../../utils';
import { PAYMENT_TYPE_LABELS, ALL_PAYMENT_TYPE_LABELS } from '../../../constants';
import OrderDetailsPaymentForm from './OrderDetailsPaymentForm';
import { toDateTimeLocalValue } from './OrderDetailsUtils';
import { useLanguage } from '../../../i18n/LanguageContext';

const OrderDetailsSummary = ({
  totalAmount,
  discountedTotalAmount,
  iskontoluToplam,
  vadeliToplam,
  toplamOdeme,
  remainingAmount,
  paidAmount,
  isPaymentCompleted,
  genelIskontoYuzde,
  vadeliYuzde,
  handleGenelIskontoYuzdeChange,
  handleVadeliYuzdeChange,
  handlePaymentCompletedChange,
  isMontajci,
  isLoading,
  isSavingPayment,
  isEditMode,
  paymentHistory,
  setCancelPaymentConfirm,
  handleSavePricingFields,
  isSavingVadeli,
  // Payment Form Props
  customerId,
  orders,
  newPaymentType,
  setNewPaymentType,
  newPaymentAmount,
  setNewPaymentAmount,
  newPaymentDate,
  setNewPaymentDate,
  isAddingPayment,
  handleAddPayment,
  // Ödeme Tamamlama Paneli Props
  showPaymentCompletePanel,
  setShowPaymentCompletePanel,
  completionAmount,
  completionPaymentType,
  setCompletionPaymentType,
  completionPaymentDate,
  setCompletionPaymentDate,
  handleConfirmPaymentCompleted,
  isCompletingPayment
}) => {
  const { t, language } = useLanguage();
  const iskYuzde = parseFloat(genelIskontoYuzde) || 0;

  // Ödeme geçmişini ödeme tarihine göre sırala (geçmiş tarihli girişler doğru yerde görünsün)
  const sortedPaymentHistory = [...(paymentHistory || [])].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return (
    <div className="total-section">
      <div className="totals-grid">
        {/* ARA TOPLAM */}
        <div className="total-item">
          <span className="total-label">{t('orderSummary.subtotal', 'Ara Toplam')}</span>
          <span className="total-value">{formatPrice(totalAmount)} €</span>
        </div>

        {/* ÜRÜN İNDİRİMLİ TOPLAM (Opsiyonel) */}
        {discountedTotalAmount !== totalAmount && (
          <div className="total-item discounted">
            <span className="total-label">{t('orderSummary.discountedSubtotal', 'Ürün İndirimli Toplam')}</span>
            <span className="total-value">{formatPrice(discountedTotalAmount)} €</span>
          </div>
        )}

        {/* GENEL İSKONTO GİRİŞİ */}
        {!isMontajci && (
          <div className="total-item iskontolu-input">
            <span className="total-label">{t('orderSummary.generalDiscount', 'Genel İskonto (%)')}</span>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={genelIskontoYuzde || ''}
              onChange={(e) => handleGenelIskontoYuzdeChange(e.target.value)}
              placeholder="%"
              className="vadeli-input-field"
            />
          </div>
        )}

        {/* İSKONTOLU TOPLAM (Sadece iskonto varsa) */}
        {iskYuzde > 0 && (
          <div className="total-item iskontolu">
            <span className="total-label">{t('orderSummary.discountedTotal', 'İskontolu Toplam')}</span>
            <span className="total-value">{formatPrice(iskontoluToplam)} €</span>
          </div>
        )}


        {/* GENEL TOPLAM (NET) */}
        <div className="total-item toplam-odeme">
          <span className="total-label">{t('orderSummary.grandTotal', 'Genel Toplam (Net)')}</span>
          <span className="total-value">{formatPrice(toplamOdeme)} €</span>
        </div>

        {/* ÖDEME DURUMU */}
        {!isMontajci && (
          <div className="total-item payment-section">
            <div className="payment-checkbox-wrapper">
              <label className="payment-checkbox-label">
                <input
                  type="checkbox"
                  checked={isPaymentCompleted || showPaymentCompletePanel}
                  onChange={(e) => handlePaymentCompletedChange(e.target.checked)}
                  disabled={isLoading || isSavingPayment || isCompletingPayment}
                  className="payment-checkbox"
                />
                <span>{t('orderSummary.paymentCompleted', 'Ödeme Tamamlandı')}</span>
              </label>
            </div>

            {/* ÖDEME TAMAMLAMA PANELİ (Checkbox'a basınca açılır) */}
            {showPaymentCompletePanel && !isPaymentCompleted && (
              <div className="payment-complete-panel">
                <p className="payment-complete-info">
                  {language === 'nl'
                    ? <>Het restantbedrag van <strong>{formatPrice(completionAmount)} €</strong> wordt geregistreerd met onderstaande datum en betaalmethode.</>
                    : <>Kalan <strong>{formatPrice(completionAmount)} €</strong> ödeme geçmişine aşağıdaki tarih ve ödeme tipiyle kaydedilecek.</>}
                </p>
                <div className="payment-add-row">
                  <div className="payment-type-select">
                    <label htmlFor="completion-payment-type">{t('orderSummary.paymentType', 'Ödeme Tipi:')}</label>
                    <select
                      id="completion-payment-type"
                      value={completionPaymentType}
                      onChange={(e) => setCompletionPaymentType(e.target.value)}
                      disabled={isCompletingPayment}
                    >
                      {Object.entries(PAYMENT_TYPE_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{t('paymentType.' + key, label)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="payment-amount-input">
                    <label htmlFor="completion-payment-date">{t('orderSummary.paymentDate', 'Ödeme Tarihi:')}</label>
                    <input
                      id="completion-payment-date"
                      type="datetime-local"
                      value={completionPaymentDate}
                      max={toDateTimeLocalValue()}
                      onChange={(e) => setCompletionPaymentDate(e.target.value)}
                      disabled={isCompletingPayment}
                    >
                    </input>
                  </div>
                  <div className="payment-complete-actions">
                    <button
                      type="button"
                      className="payment-complete-cancel-btn"
                      onClick={() => setShowPaymentCompletePanel(false)}
                      disabled={isCompletingPayment}
                    >
                      {t('orderSummary.cancel', 'Vazgeç')}
                    </button>
                    <button
                      type="button"
                      className="payment-add-btn"
                      onClick={handleConfirmPaymentCompleted}
                      disabled={isCompletingPayment || !completionPaymentDate}
                    >
                      {isCompletingPayment ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <><i className="fas fa-check"></i> {t('orderSummary.completePayment', 'Ödemeyi Tamamla')}</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TOPLAM ÖDENEN */}
        {!isMontajci && (
          <div className="total-item paid-amount">
            <span className="total-label">{t('orderSummary.totalPaid', 'Toplam Ödenen')}</span>
            <span className="total-value">{formatPrice(parseFloat(paidAmount) || 0)} €</span>
          </div>
        )}

        {/* KALAN ÖDEME */}
        {!isMontajci && (
          <div className="total-item remaining-amount">
            <span className="total-label">{t('orderSummary.remainingPayment', 'Kalan Ödeme')}</span>
            <span className={`total-value ${remainingAmount === 0 ? 'payment-completed' : ''}`}>
              {remainingAmount === 0 ? (
                <>
                  <i className="fas fa-check-circle"></i>
                  {isPaymentCompleted ? t('orderSummary.paymentReceived', 'Ödeme Alındı') : '0.00'}
                </>
              ) : (
                <>{formatPrice(remainingAmount)} €</>
              )}
            </span>
          </div>
        )}
      </div>

      {/* YENİ ÖDEME EKLE FORMU (Ödeme Geçmişinin Üstünde) */}
      {!isMontajci && (
        <OrderDetailsPaymentForm
          isEditMode={isEditMode}
          customerId={customerId}
          orders={orders}
          isMontajci={isMontajci}
          isPaymentCompleted={isPaymentCompleted}
          newPaymentType={newPaymentType}
          setNewPaymentType={setNewPaymentType}
          newPaymentAmount={newPaymentAmount}
          setNewPaymentAmount={setNewPaymentAmount}
          newPaymentDate={newPaymentDate}
          setNewPaymentDate={setNewPaymentDate}
          isAddingPayment={isAddingPayment}
          handleAddPayment={handleAddPayment}
          isLoading={isLoading}
        />
      )}

      {/* ÖDEME GEÇMİŞİ */}
      {!isMontajci && paymentHistory.length > 0 && (
        <div className="payment-history-section">
          <h5><i className="fas fa-history"></i> {t('orderSummary.paymentHistory', 'Ödeme Geçmişi')}</h5>
          <div className="payment-history-list">
            {sortedPaymentHistory.map((payment, index) => (
              <div key={payment.id || index} className="payment-history-item">
                <div className="payment-history-info">
                  <span className={`payment-type-badge payment-type-${payment.type}`}>
                    {t('paymentType.' + payment.type, ALL_PAYMENT_TYPE_LABELS?.[payment.type] || PAYMENT_TYPE_LABELS[payment.type] || payment.type)}
                  </span>
                  <span className="payment-amount">{formatPrice(payment.amount)} €</span>
                  {payment.source === 'payment_completed' && (
                    <span className="payment-completed-tag" title="Ödeme Tamamlandı ile eklendi">
                      <i className="fas fa-check-circle"></i> {language === 'nl' ? 'Voltooiing' : 'Tamamlama'}
                    </span>
                  )}
                </div>
                <div className="payment-history-right">
                  <div className="payment-history-date">
                    {new Date(payment.date).toLocaleDateString(language === 'nl' ? 'nl-NL' : 'tr-TR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                  <button
                    type="button"
                    className="payment-cancel-btn"
                    onClick={() => setCancelPaymentConfirm(payment)}
                    title={t('orderSummary.cancelPayment', 'İptal')}
                  >
                    <i className="fas fa-trash-alt"></i>
                    {t('orderSummary.cancelPayment', 'İptal')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KAYDET BUTONU */}
      {isEditMode && !isMontajci && (
        <div className="vadeli-save-section">
          <button
            type="button"
            onClick={handleSavePricingFields}
            className="vadeli-save-btn"
            disabled={isSavingVadeli || isLoading}
          >
            {isSavingVadeli ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>{t('orderSummary.saving', 'Kaydediliyor...')}</span>
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                <span>{t('orderSummary.savePricing', 'Fiyat Bilgilerini Kaydet')}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderDetailsSummary;
